import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { Button } from 'components/button/Button';
import { FieldArray } from 'formik';
import { Formik } from 'components/form/formik';
import diff from 'object-diff';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import * as Yup from 'yup';
import { InputField } from '../../../components/input/InputField';
import { ISimpleSelectItem } from '../../../components/select/SimpleSelect';
import { SimpleSelectField } from '../../../components/select/SimpleSelectField';
import { Country } from 'src/types/enums';
import { updateGeneralSettings } from '../models';
import { COUNTRY_VALUES } from '../../../utils/address';
import { getDisplayError } from '../../../utils/get-display-error';
import { useAllGeneralSettings } from '../all-settings-context';
import { captureException } from '@sentry/react';

const generalSettingsSchema = Yup.object().shape({
  companyName: Yup.string().min(1, 'Vereist').required('Vereist'),
  street: Yup.string().min(1, 'Vereist').required('Vereist'),
  streetNumber: Yup.string().min(1, 'Vereist').required('Vereist'),
  postalCode: Yup.string().min(1, 'Vereist').required('Vereist'),
  city: Yup.string().min(1, 'Vereist').required('Vereist'),
  country: Yup.mixed().required('Vereist'),
  phoneNumber: Yup.string().phone().required('Vereist'),
  generalEmail: Yup.string().email('Ongeldig email').required('Vereist'),
  transportEmail: Yup.string().email('Ongeldig email').required('Vereist'),
  financeEmail: Yup.string().email('Ongeldig email').required('Vereist'),
  invoiceBccEmail: Yup.string().email('Ongeldig email').required('Vereist'),
  vatNumber: Yup.string().vatNumber().required('Vereist'),
  numberPrefix: Yup.string(),
  accountRequestContacts: Yup.array().of(Yup.string().email('Ongeldig email')).required('Vereist'),
  bankAccounts: Yup.array()
    .of(
      Yup.object().shape({
        name: Yup.string().min(1, 'Vereist').required('Vereist'),
        iban: Yup.string().min(1, 'Vereist').required('Vereist'),
        bic: Yup.string().min(1, 'Vereist').required('Vereist'),
      }),
    )
    .required('Vereist'),
});

interface IGeneralSettingsValues {
  companyName: string;
  street: string;
  streetNumber: string;
  postalCode: string;
  city: string;
  country: ISimpleSelectItem;
  phoneNumber: string;
  generalEmail: string;
  transportEmail: string;
  financeEmail: string;
  invoiceBccEmail: string;
  vatNumber: string;
  numberPrefix: string;
  auditLogRetentionDays: string;
  accountRequestContacts: string[];
  bankAccounts: Array<{ name: string; iban: string; bic: string }>;
}

const GeneralSettingsPage = () => {
  const { settings } = useAllGeneralSettings();

  const initialValues: IGeneralSettingsValues = useMemo(() => {
    return {
      companyName: settings.companyName,
      vatNumber: settings.vatNumber,
      companyNumber: settings.companyNumber,
      street: settings.street,
      streetNumber: settings.streetNumber,
      postalCode: settings.postalCode,
      city: settings.city,
      country: COUNTRY_VALUES.find((v) => v.key === settings.country)!,
      phoneNumber: settings.phoneNumber,
      generalEmail: settings.generalEmail,
      transportEmail: settings.transportEmail,
      financeEmail: settings.financeEmail,
      invoiceBccEmail: settings.invoiceBccEmail,
      cmrEmail: settings.cmrEmail,
      numberPrefix: settings.numberPrefix,
      auditLogRetentionDays: '' + settings.auditLogRetentionDays,
      accountRequestContacts: settings.accountRequestContacts,
      bankAccounts: settings.bankAccounts,
    };
  }, [settings]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={generalSettingsSchema}
      onSubmit={async (values) => {
        try {
          const patch: Partial<typeof values> = diff(initialValues, values);
          const { country, auditLogRetentionDays, ...otherValues } = patch;
          await updateGeneralSettings({
            country: country?.key as Country,
            auditLogRetentionDays: auditLogRetentionDays ? +auditLogRetentionDays : undefined,
            accountRequestContacts: otherValues.accountRequestContacts?.map((v) => v.trim()).filter(Boolean) ?? [],
            ...otherValues,
          });
          toast.success('Instellingen aangepast');
        } catch (err: any) {
          captureException(err);
          toast.error('Kon algemene instellingen niet aanpassen: ' + getDisplayError(err));
        }
      }}
    >
      {({ handleSubmit, isSubmitting, values }) => {
        return (
          <form onSubmit={handleSubmit} className="grid gap-4">
            <div className="card p-5">
              <InputField labelText="Naam" type="text" name="companyName" />
              <InputField labelText="BTW Nummer" type="text" name="vatNumber" />
              <InputField labelText="Ondernemingsnummer" type="text" name="companyNumber" />
              <div className="flex gap-4">
                <div className="flex-4">
                  <InputField labelText="Straat" type="text" name="street" />
                </div>
                <div className="flex-1">
                  <InputField labelText="Nr" type="text" name="streetNumber" />
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-1">
                  <InputField labelText="Postcode" type="text" name="postalCode" />
                </div>
                <div className="flex-2">
                  <InputField labelText="Gemeente" type="text" name="city" />
                </div>
                <div className="flex-1">
                  <SimpleSelectField labelText="Landcode" items={COUNTRY_VALUES} name="country" />
                </div>
              </div>

              <InputField labelText="Prefix nummering" type="text" name="numberPrefix" />

              <InputField labelText="Audit logs bewaartijd in dagen" type="number" name="auditLogRetentionDays" />

              <InputField labelText="E-mail algemeen" type="email" name="generalEmail" />
              <InputField labelText="E-mail financieel" type="email" name="financeEmail" />
              <InputField labelText="E-mail transport" type="email" name="transportEmail" />
              <InputField labelText="E-mail cmr" type="email" name="cmrEmail" />
              <InputField labelText="BCC E-mail facturatie" type="email" name="invoiceBccEmail" />
              <InputField labelText="Telefoonnummer" type="text" name="phoneNumber" />

              <FieldArray
                name="accountRequestContacts"
                render={(arrayHelpers) => (
                  <div className="mt-2 border-t border-border pt-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="heading-four">Contacten voor toegangsverzoek klanten- en vervoerdersportaal</div>
                      <Button onTrigger={() => arrayHelpers.push('')}>Voeg email toe</Button>
                    </div>
                    {values.accountRequestContacts.map((interval, index) => (
                      <div className="flex items-center gap-2" key={index}>
                        <InputField labelText="Contact email" type="email" name={`accountRequestContacts.${index}`} />
                        <div>
                          <Button type="button" color="danger" onTrigger={() => arrayHelpers.remove(index)}>
                            <TrashIcon className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              />

              <FieldArray
                name="bankAccounts"
                render={(arrayHelpers) => (
                  <div className="mt-2 border-t border-border pt-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="heading-four">Bankrekeningen op facturen en herinneringen</div>
                      <Button onTrigger={() => arrayHelpers.push({ name: '', iban: '', bic: '' })}>
                        Voeg rekening toe
                      </Button>
                    </div>
                    {values.bankAccounts.map((_account, index) => (
                      <div className="flex items-center gap-2" key={index}>
                        <div className="flex-2">
                          <InputField labelText="Bank" type="text" name={`bankAccounts.${index}.name`} />
                        </div>
                        <div className="flex-3">
                          <InputField labelText="IBAN" type="text" name={`bankAccounts.${index}.iban`} />
                        </div>
                        <div className="flex-2">
                          <InputField labelText="BIC" type="text" name={`bankAccounts.${index}.bic`} />
                        </div>
                        <div>
                          <Button type="button" color="danger" onTrigger={() => arrayHelpers.remove(index)}>
                            <TrashIcon className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              />
            </div>

            <div>
              <Button
                type="submit"
                color="primary"
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
                iconLeft={<PlusIcon className="button-icon" />}
              >
                Pas instellingen aan
              </Button>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

export default GeneralSettingsPage;
